import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Stethoscope, StickyNote, FlaskConical, Lock, FileText,
    Filter, AlertTriangle, Sparkles, Loader2, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const TYPE_META = {
    consultation: { label: "Consulta", icon: Stethoscope, color: "#1E3F33", bg: "#E5EEE7" },
    evolution: { label: "Evolução", icon: StickyNote, color: "#4A6B58", bg: "#EAF1EC" },
    exam: { label: "Exame", icon: FlaskConical, color: "#8A6A15", bg: "#FBF4E5" },
    addendum: { label: "Adendo", icon: FileText, color: "#5B3F72", bg: "#EDE4FF" },
    care_plan: { label: "Plano de Cuidado", icon: Sparkles, color: "#2A5773", bg: "#E1EBF3" },
    calculator: { label: "Escala", icon: Sparkles, color: "#7E4E1E", bg: "#F5EEE0" },
    prescription: { label: "Receita", icon: FileText, color: "#B33A3A", bg: "#FBEEEE" },
};

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("pt-BR", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

function monthLabel(ym) {
    if (!ym || ym === "sem-data") return "Sem data";
    const [y, m] = ym.split("-");
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

export default function ClinicalTimeline({ patientId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        consultation: true, evolution: true, exam: true, addendum: true, care_plan: true,
        calculator: true, prescription: true,
    });

    useEffect(() => {
        setLoading(true);
        api.get(`/patients/${patientId}/timeline`)
            .then((r) => setItems(r.data.items || []))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [patientId]);

    const filtered = useMemo(() => items.filter((it) => filters[it.type]), [items, filters]);
    const groups = useMemo(() => {
        const byMonth = new Map();
        for (const it of filtered) {
            const key = (it.date || "").slice(0, 7) || "sem-data";
            if (!byMonth.has(key)) byMonth.set(key, []);
            byMonth.get(key).push(it);
        }
        return Array.from(byMonth.entries())
            .sort((a, b) => b[0].localeCompare(a[0]));
    }, [filtered]);

    if (loading) {
        return (
            <div className="card-earthy flex items-center gap-2 text-sm text-muted-clinical">
                <Loader2 className="animate-spin" size={14} /> Carregando linha do tempo...
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="clinical-timeline">
            <div className="card-earthy">
                <div className="flex items-start gap-2 flex-wrap">
                    <div className="flex items-center gap-1 text-xs text-brand-secondary font-medium">
                        <Filter size={12} /> Filtrar:
                    </div>
                    {Object.entries(TYPE_META).map(([key, meta]) => {
                        const active = filters[key];
                        return (
                            <button
                                key={key}
                                onClick={() => setFilters((f) => ({ ...f, [key]: !f[key] }))}
                                className="text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors"
                                style={{
                                    background: active ? meta.color : "transparent",
                                    color: active ? "#FFF" : meta.color,
                                    border: `1px solid ${meta.color}`,
                                }}
                                data-testid={`timeline-filter-${key}`}
                            >
                                {meta.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {groups.length === 0 ? (
                <div className="card-earthy text-sm text-muted-clinical text-center py-10">
                    Nenhum evento encontrado com estes filtros.
                </div>
            ) : (
                groups.map(([month, entries]) => (
                    <div key={month}>
                        <div className="flex items-center gap-2 mb-2">
                            <div className="h-px flex-1 bg-[var(--border-organic)]"></div>
                            <span className="text-xs uppercase tracking-widest text-brand-secondary font-medium">
                                {monthLabel(month)}
                            </span>
                            <div className="h-px flex-1 bg-[var(--border-organic)]"></div>
                        </div>
                        <div className="relative pl-6">
                            <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-[var(--border-organic)]"></div>
                            <div className="space-y-2">
                                {entries.map((it) => (
                                    <TimelineItem key={it.id} item={it} />
                                ))}
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

function TimelineItem({ item }) {
    const meta = TYPE_META[item.type] || TYPE_META.consultation;
    const Icon = meta.icon;
    const link =
        item.type === "exam"
            ? null
            : item.consultation_id
                ? `/consultations/${item.consultation_id}`
                : null;

    const inner = (
        <div
            className="card-earthy border-l-4 relative"
            style={{ borderLeftColor: meta.color }}
            data-testid={`timeline-item-${item.type}`}
        >
            <div
                className="absolute -left-[27px] top-4 w-4 h-4 rounded-full flex items-center justify-center"
                style={{ background: meta.color }}
                aria-hidden="true"
            >
                <Icon size={9} className="text-white" />
            </div>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                            style={{ background: meta.bg, color: meta.color }}
                        >
                            {meta.label}
                        </span>
                        <h3 className="font-medium text-brand line-clamp-1">{item.title}</h3>
                        {item.locked && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-[var(--brand-accent)]/15 text-[var(--brand-accent)] rounded-full font-semibold flex items-center gap-0.5">
                                <Lock size={9} /> Encerrada
                            </span>
                        )}
                        {item.abnormal_count > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#FBEEEE] text-[#8A1A1A] rounded-full font-semibold flex items-center gap-0.5">
                                <AlertTriangle size={9} /> {item.abnormal_count} alteração{item.abnormal_count > 1 ? "es" : ""}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-muted-clinical mt-1">{fmtDateTime(item.date)}</p>
                    {item.summary && (
                        <p className="text-sm text-brand-secondary mt-2 whitespace-pre-wrap line-clamp-2">
                            {item.summary}
                        </p>
                    )}
                    {item.author && (
                        <p className="text-[11px] text-muted-clinical mt-1 italic">por {item.author}</p>
                    )}
                </div>
                {link && <ChevronRight className="text-brand shrink-0 mt-1" size={16} />}
            </div>
        </div>
    );

    if (link) return <Link to={link} className="block hover:opacity-90 transition-opacity">{inner}</Link>;
    return inner;
}
